import { Injectable, inject } from '@angular/core'

import { Observable, map } from 'rxjs'

import {
  CreateDataMapperPayload,
  DataMapper,
  DataMappingResponse,
  ResourceDataSlice,
  ResourceNodeSlice,
  SliceType,
  SourceElementType,
  TestMappingPayload,
  UpdateDataMapperPayload,
} from '@flowx/designer/modules/core/dictionary/data-mapping.dictionary'
import {
  DataModelEntity,
  NotificationTemplateSlicesResponse,
  ProcessNodeSlicesResponse,
  WorkflowNodeSlicesResponse,
} from '@flowx/designer/modules/core/dictionary/data-model.dictionary'

import { ApiHttpProxyService, ForwardProxyMicroservice } from './api-http-proxy.service'

import { ApplicationResourceType, ProxyApiAction } from '../entities/application.entity'
import {
  ApplicationApiParameters,
  UrlParametersStoreService,
} from '../services/url-parameters-store.service'

@Injectable({
  providedIn: 'root',
})
export class DataMappingApiService extends ApiHttpProxyService {
  #url = 'api/process'
  #urlParametersStoreService = inject(UrlParametersStoreService)
  #processApiPath = inject<string>('PROCESS_API_PATH' as any)

  getProcessNodesSlices(
    processResourceId: string,
    application: ApplicationApiParameters
  ): Observable<{ resourceContent: ProcessNodeSlicesResponse }> {
    const { appId: parentAppId } = this.#urlParametersStoreService.urlParameters
    const { appId, appVersionId } = application
    const commonUrl = `app/${appId}/app-version/${appVersionId}/rt/process/ri/${processResourceId}/admin/api`

    if (parentAppId === appId) {
      return this.httpClient.get(
        `${this.getBaseUrl()}/appmanager/api/resource/${commonUrl}/process/node-data-slices`,
        {
          params: this.addParams({ cloneResource: false }),
        }
      ) as Observable<{ resourceContent: ProcessNodeSlicesResponse }>
    }

    return this.httpClient.get(
      `${this.getBaseUrl()}/rtm/api/runtime-internal/${commonUrl}/runtime-internal/process/node-data-slices`
    ) as Observable<{ resourceContent: ProcessNodeSlicesResponse }>
  }

  getWorkflowNodesSlices(
    resourceId: string,
    application: ApplicationApiParameters,
    sliceType?: SliceType
  ): Observable<{
    resourceContent: WorkflowNodeSlicesResponse
  }> {
    const { appId: parentAppId } = this.#urlParametersStoreService.urlParameters

    const { appId, appVersionId } = application
    const url = `app/${appId}/app-version/${appVersionId}/rt/workflow/ri/${resourceId}/integration/api`

    if (parentAppId === appId) {
      return this.httpClient.get(
        `${this.getBaseUrl()}/appmanager/api/resource/${url}/workflows/node-data-slices`,
        {
          params: this.addParams({ sliceType, cloneResource: false }),
        }
      ) as Observable<{ resourceContent: WorkflowNodeSlicesResponse }>
    }

    return this.httpClient.get(
      `${this.getBaseUrl()}/rtm/api/runtime-internal/${url}/runtime-internal/workflows/node-data-slices`,
      {
        params: this.addParams({ sliceType }),
      }
    ) as Observable<{ resourceContent: WorkflowNodeSlicesResponse }>
  }

  getNotificationTemplateSlices(
    resourceId: string,
    application: ApplicationApiParameters,
    sliceType: SliceType
  ): Observable<{ resourceContent: NotificationTemplateSlicesResponse }> {
    const { appId: parentAppId } = this.#urlParametersStoreService.urlParameters
    const { appId, appVersionId } = application
    const url = `app/${appId}/app-version/${appVersionId}/rt/notification-template/ri/${resourceId}/${ForwardProxyMicroservice.NotificationPlugin}/api`
    const endpoint = 'notification-template/data-slice/data-model-and-slices'

    const request$ = (
      parentAppId === appId
        ? this.httpClient.get(`${this.getBaseUrl()}/appmanager/api/resource/${url}/${endpoint}`)
        : this.httpClient.get(
            `${this.getBaseUrl()}/rtm/api/runtime-internal/${url}/runtime-internal/${endpoint}`
          )
    ) as Observable<{
      resourceContent: {
        dataEntities?: DataModelEntity[]
        dataSlices?: ResourceDataSlice[]
        slices?: ResourceDataSlice[]
      }
    }>

    return request$.pipe(
      map(({ resourceContent }) => {
        const dataSlices = resourceContent?.dataSlices ?? resourceContent?.slices ?? []
        const slice = dataSlices.find((dataSlice) => dataSlice.type === sliceType)
        const node: ResourceNodeSlice = {
          name: '',
          nodeFlowxUuid: resourceId,
          nodeId: resourceId,
          nodeType: null as ResourceNodeSlice['nodeType'],
          slices: slice ? [slice] : [],
        }
        return {
          resourceContent: {
            notificationTemplateResourceId: resourceId,
            nodes: [node],
            dataEntities: resourceContent?.dataEntities ?? [],
          },
        }
      })
    )
  }

  getElementMappings(
    elementFlowxUuid: string,
    elementType: SourceElementType
  ): Observable<{ resourceContent: DataMappingResponse }> {
    const urlPathByType = this.getMappingPathByResourceType(elementType)
    return this.get(`${this.#url}/${urlPathByType}/${elementFlowxUuid}/mappings`, {
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.Process,
    })
  }

  createActionMapping(
    elementFlowxUuid: string,
    resourceType: ApplicationResourceType,
    elementType: SourceElementType,
    payload: CreateDataMapperPayload
  ): Observable<{ resourceContent: DataMapper }> {
    const urlPathByType = this.getMappingPathByResourceType(elementType)
    return this.patch(`${this.#url}/${urlPathByType}/${elementFlowxUuid}/mappings`, payload, {
      action: ProxyApiAction.CREATE,
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.Process,
      resourceTypesToIncludeMappingFor: [ApplicationResourceType.ProjectDataType, resourceType],
    })
  }

  updateActionMapping(
    elementFlowxUuid: string,
    resourceType: ApplicationResourceType,
    elementType: SourceElementType,
    payload: UpdateDataMapperPayload
  ): Observable<{ resourceContent: DataMapper }> {
    const urlPathByType = this.getMappingPathByResourceType(elementType)
    return this.patch(`${this.#url}/${urlPathByType}/${elementFlowxUuid}/mappings`, payload, {
      action: ProxyApiAction.UPDATE,
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.Process,
      resourceTypesToIncludeMappingFor: [ApplicationResourceType.ProjectDataType, resourceType],
    })
  }

  testMapping(payload: TestMappingPayload): Observable<{
    result: Record<string, any>
    errors: Record<string, string>
  }> {
    const { appId } = this.#urlParametersStoreService.urlParameters
    const processApiPath = this.#processApiPath
      ? this.#processApiPath.replace(/^\/?/, '') + '/'
      : ''
    const url = `${processApiPath}api/test/data-mappers`
    return this.httpClient.post<{
      result: Record<string, any>
      errors: Record<string, string>
    }>(`${this.getBaseUrl()}/${url}`, {
      ...payload,
      appId,
    })
  }
  getMappingPathByResourceType(elementType: SourceElementType): string {
    return {
      [SourceElementType.DATA_STREAM_NODE_VALUE]: 'nodes/data-streams',
      [SourceElementType.ACTION]: 'actions',
    }[elementType]
  }
}
